
//#region imports 
import { useEffect, useState } from "react";

// bootstrap
import { Col, Row } from "react-bootstrap";

// tremor 
import { Title as TremorTitle, Subtitle, Flex } from "@tremor/react";

// redux
import { useDispatch, useSelector } from "react-redux";
import { resetStatus, selectStaffEquipments, selectStaffEquipmentsStatus } from "_redux/slices/timeline/staff-equipments";

// custom component
import { Card, SearchBar } from "_atoms";
import { ActionsModal } from "_components/molecules";
import AddStaffEquipments from "./add-staff-equipments";

// styles
import { PRM_LIGHT_BLUE } from "_styles/colors";
import { SCALE_8 } from "_styles/spacing";

// icons
import { FaCirclePlus } from "react-icons/fa6";
import { MdEdit } from "react-icons/md";

//#endregion

const StaffEquipments = () => {

    //#region redux
    const dispatch = useDispatch();

    const equipmentData = useSelector(selectStaffEquipments);                           // get staff equipments data
    const equipmentDataStatus = useSelector(selectStaffEquipmentsStatus);               // get staff equipments data status
    
    //#endregion

    //#region local states 
    const { vehicleList, wheelChairList } = equipmentData;                             // destructure vehicle and wheelchair lists from equipment data
    const noEquipmentsProvided = !equipmentData || (vehicleList?.length === 0 && wheelChairList?.length === 0);     // check if no equipments are provided

    // search state
    const [searchValue, setSearchValue] = useState("");

    // modal state
    const [showModal, setShowModal] = useState(false);

    //#endregion

    //#region functions

    // set search
    const handleSearchEquipment = (searchVal) => {
        setSearchValue(searchVal);
    }

    // toggle modal
    const triggerModal = () => {
        setShowModal(!showModal)
    }

    // filter valid items and search
    const filterValidItems = (list = []) =>
        list.filter(
            (item) =>
                item &&
                typeof item === "object" &&
                Object.keys(item).length > 0 &&
                typeof item.title === "string" &&
                item.title.trim() !== ""
    );

    // filter list based on search value
    const filterList = (list = []) =>
        filterValidItems(list).filter((item) =>
            item.title.toLowerCase().includes(searchValue.toLowerCase())
        );

    //#endregion

    // reset status on success and on render
    useEffect(() => {
        if (equipmentDataStatus === "succeeded") {
            dispatch(resetStatus())
        }
    }, [equipmentDataStatus])

    const renderList = (list) => {
        const filtered = filterList(list);
        if (filtered.length === 0) return null;
        
        return (
            <ul className="list-group mb-3">
                {filtered.map((item) => (
                    <li 
                        key={item.id} 
                        className="list-group-item"
                        style={{ fontSize: 14 }}
                    >
                        {item.title}
                    </li>
                ))}
            </ul>
        );
    };

    return (
        <>
            <Card>

                {/* content */}
                <Row>
                    <Col>
                        <Flex>

                            {/* title + description */}
                            <div>
                                <TremorTitle>Staff Equipment List</TremorTitle>
                                <Subtitle>Add, edit and search for airport vehicles and equipment</Subtitle>
                            </div>

                            {/* search + add */}
                            <div className="d-flex gap-2">
                                
                                <SearchBar 
                                    onChange={(val) => handleSearchEquipment(val.target.value)} 
                                    placeholder={"Search Equipment"}
                                    value={searchValue} 
                                    isTimeline={false}
                                />
                                
                                {/* add equipment */}
                                <FaCirclePlus
                                    onClick={triggerModal}
                                    style={{ marginLeft: SCALE_8, cursor: "pointer" }}
                                    color={"lightgrey"}
                                    size={28}
                                /> 

                            </div>
                        </Flex>
                    </Col>
                </Row>

                {/* no equipments added yet message + buton*/}
                {noEquipmentsProvided ?
                    <div style={{ opacity: 0.9, justifyContent:'center' }} className='mt-5'>

                        {/* no equipments added yet */}
                        <p className="mt-3" style={{ textAlign: "center", color: PRM_LIGHT_BLUE }}>
                            {"Click on the plus icon to add vehicle equipment"}
                        </p>

                        {/* add equipment */}
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <FaCirclePlus
                                onClick={triggerModal}
                                style={{ cursor: "pointer" }}
                                color={PRM_LIGHT_BLUE}
                                size={28}
                            /> 
                        </div>
                    </div> 
                    :

                    /* equipment lists */
                    
                    <Row style={{ opacity: 0.9 }} className="mt-4 g-4">
                        {vehicleList?.length > 0 && (
                            <Col md={6}>
                                <h6 className="fw-bold text-secondary mb-2">Vehicles</h6>
                                {renderList(vehicleList)}
                            </Col>
                        )}

                        {wheelChairList?.length > 0 && (
                            <Col md={6}>
                                <h6 className="fw-bold text-secondary mb-2">Wheelchairs</h6>
                                {renderList(wheelChairList)}
                            </Col>
                        )}
                    </Row>
                }

            </Card>

            {/* add / edit equipments modal */}
            <ActionsModal
                show={showModal}
                handleClose={triggerModal}
                title={"Equipment Management"}
                subtitle={"Add or edit staff vehicles and wheelchairs"}
                minWidth="700px"
                maxWidth="700px"
                isTimeline={false}>
                
                <AddStaffEquipments 
                    addForm={noEquipmentsProvided} 
                    wheelchairList={wheelChairList} 
                    vehicleList={vehicleList} 
                    setShowModal={setShowModal} 
                />
                
            </ActionsModal>
        </>


    )
}

export default StaffEquipments